/**
 * Requirement Type Selector Component
 *
 * Allows user to select the type of requirement they want to submit.
 */

import React from 'react';
import type { RequirementType } from '../types';
import clsx from 'clsx';

// Icon components
const BugIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M8 2L6 6H18L16 2" />
    <path d="M12 6V22" />
    <path d="M6 12H2" />
    <path d="M22 12H18" />
    <path d="M6 18H2" />
    <path d="M22 18H18" />
    <ellipse cx="12" cy="14" rx="6" ry="8" />
  </svg>
);

const FeatureIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const ChangeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 2L3 22" />
    <path d="M21 22L3 2" />
  </svg>
);

const EnhancementIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2L12 6" />
    <path d="M12 18L12 22" />
    <path d="M4.93 4.93L7.76 7.76" />
    <path d="M16.24 16.24L19.07 19.07" />
    <path d="M2 12L6 12" />
    <path d="M18 12L22 12" />
    <path d="M4.93 19.07L7.76 16.24" />
    <path d="M16.24 7.76L19.07 4.93" />
  </svg>
);

const PerformanceIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </svg>
);

const UsabilityIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const DocsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const InfoIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

interface TypeOption {
  type: RequirementType;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const TYPE_OPTIONS: TypeOption[] = [
  {
    type: 'bug_fix',
    label: 'Bug Report',
    description: 'Something is not working correctly',
    icon: <BugIcon />,
    color: 'psc-type--bug',
  },
  {
    type: 'new_feature',
    label: 'New Feature',
    description: 'Request a new capability',
    icon: <FeatureIcon />,
    color: 'psc-type--feature',
  },
  {
    type: 'enhancement',
    label: 'Enhancement',
    description: 'Improve an existing feature',
    icon: <EnhancementIcon />,
    color: 'psc-type--enhancement',
  },
  {
    type: 'change_request',
    label: 'Change Request',
    description: 'Modify existing behavior',
    icon: <ChangeIcon />,
    color: 'psc-type--change',
  },
  {
    type: 'performance',
    label: 'Performance Issue',
    description: 'Something is slow or resource-intensive',
    icon: <PerformanceIcon />,
    color: 'psc-type--performance',
  },
  {
    type: 'usability',
    label: 'Usability Feedback',
    description: 'UX/UI improvement suggestion',
    icon: <UsabilityIcon />,
    color: 'psc-type--usability',
  },
  {
    type: 'documentation',
    label: 'Documentation',
    description: 'Documentation is missing or incorrect',
    icon: <DocsIcon />,
    color: 'psc-type--docs',
  },
  {
    type: 'info_request',
    label: 'Question',
    description: 'Ask a question or request information',
    icon: <InfoIcon />,
    color: 'psc-type--info',
  },
];

interface TypeSelectorProps {
  selectedType: RequirementType | null;
  onSelect: (type: RequirementType) => void;
}

export function TypeSelector({ selectedType, onSelect }: TypeSelectorProps) {
  return (
    <div className="psc-type-selector">
      <h3 className="psc-type-selector__title">What would you like to report?</h3>
      <div className="psc-type-selector__grid">
        {TYPE_OPTIONS.map((option) => (
          <button
            key={option.type}
            onClick={() => onSelect(option.type)}
            className={clsx(
              'psc-type-option',
              option.color,
              selectedType === option.type && 'psc-type-option--selected'
            )}
            data-testid={`type-${option.type}`}
          >
            <div className="psc-type-option__icon">{option.icon}</div>
            <div className="psc-type-option__content">
              <span className="psc-type-option__label">{option.label}</span>
              <span className="psc-type-option__description">{option.description}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
